import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  Mic,
  StickyNote,
  MessageSquare,
  Trash2,
  X,
  Type,
  Video,
  Square,
  ImagePlus,
  Clapperboard,
} from "lucide-react";
import CoachVoiceButton from "../AICoach/CoachVoiceButton";
import {
  createChampionshipPlayerNote,
  deleteChampionshipPlayerNote,
} from "../funtionsBD/competitionService";
import { currentAppLanguage } from "../../utils/appLanguages";
import { useLiveVoiceTranscript } from "../../hooks/useLiveVoiceTranscript";
import { checkBrowserCapability } from "../../voice";
import { ip_back } from "../../config";
import { useBrand } from "../../context/BrandContext";
import "./ChampionshipPlayerNotes.css";

function noteMatchesPlayer(note, player, side) {
  if (!note || !player) return false;
  if (note.about_user_id && player.user_id) {
    return String(note.about_user_id) === String(player.user_id);
  }
  if (note.about_player_id && player.player_id) {
    return String(note.about_player_id) === String(player.player_id);
  }
  if (
    note.about_side === side &&
    note.about_seat != null &&
    player.seat != null &&
    Number(note.about_seat) === Number(player.seat)
  ) {
    return true;
  }
  const a = String(note.about_display_name || "")
    .trim()
    .toLowerCase();
  const b = String(player.name || player.display_name || "")
    .trim()
    .toLowerCase();
  return Boolean(a && b && a === b);
}

function noteMatchesGroup(note, players, side) {
  return (players || []).some((p) => noteMatchesPlayer(note, p, side));
}

function initials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function resolveMediaUrl(url) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw) || raw.startsWith("blob:")) return raw;
  const root = String(ip_back || "").replace(/\/?$/, "/");
  return `${root}${raw.replace(/^\//, "")}`;
}

function resolveNotesPortalRoot() {
  if (typeof document === "undefined") return null;
  const fs =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement;
  if (fs instanceof Element) {
    if (fs === document.documentElement) return document.body;
    return fs;
  }
  return document.body;
}

function stopStream(stream) {
  try {
    stream?.getTracks?.().forEach((t) => t.stop());
  } catch {
    /* ignore */
  }
}

function playerLabel(player) {
  return player?.name || player?.display_name || "—";
}

function readStoredUserId() {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = JSON.parse(localStorage.getItem("user") || "null");
    return raw?.user_id || null;
  } catch {
    return null;
  }
}

function isViewerPlayer(player, viewerUserId) {
  if (!viewerUserId || !player?.user_id) return false;
  return String(player.user_id) === String(viewerUserId);
}

function namedPlayers(list) {
  return (list || []).filter(
    (p) => p?.name && !/^Seat\s/i.test(String(p.name))
  );
}

function playerRatingMeta(player, t) {
  const bits = [];
  if (player?.classement) bits.push(String(player.classement));
  const pts = Number(player?.ranking_points);
  if (player?.ranking_points != null && Number.isFinite(pts)) {
    bits.push(
      t("cmRatingPoints", "{{n}} pts", { n: Math.round(pts) })
    );
  }
  return bits.join(" · ");
}

/**
 * Private notes + shared feedback for club players on a completed rubber.
 * Compact icon opens a dialog (text / audio / video). Doubles = one button.
 */
export default function ChampionshipPlayerNotes({
  matchId = null,
  rubberId = null,
  side,
  players = [],
  notes = [],
  onChanged,
  align = "left",
  primaryColor = null,
  createNote = null,
  deleteNote = null,
  viewerUserId = null,
  opponents = [],
  matchMediaCount = 0,
  onPlayMatchMedia = null,
}) {
  const { t, i18n } = useTranslation();
  const { brand, themeMode } = useBrand();
  const accent = primaryColor || brand?.color_primary || "#0877bc";
  const isLightTheme = themeMode === "light";
  const brandVars = {
    "--cm-primary": accent,
    "--cm-note-primary": accent,
    "--cm-note-primary-dark": brand?.color_primary_dark || accent,
    "--cm-note-primary-light": brand?.color_primary_light || accent,
    "--cm-note-secondary": brand?.color_secondary || accent,
    "--cm-note-accent": brand?.color_accent || "#FFEFDD",
    "--cm-note-surface": brand?.color_surface || (isLightTheme ? "#ffffff" : "#121826"),
    "--cm-note-bg": brand?.color_background || (isLightTheme ? "#f4f5f6" : "#0b0f19"),
    "--cm-note-text": isLightTheme
      ? brand?.color_text_primary || "#1e1e1e"
      : "#f8fafc",
    "--cm-note-muted": isLightTheme
      ? brand?.color_text_secondary || "#64748b"
      : "#94a3b8",
  };
  const [openPlayers, setOpenPlayers] = useState(null);
  const [kind, setKind] = useState("feedback");
  const [mode, setMode] = useState("text");
  const [draft, setDraft] = useState("");
  const [source, setSource] = useState("text");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [mediaBlob, setMediaBlob] = useState(null);
  const [mediaPreview, setMediaPreview] = useState("");
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [portalRoot, setPortalRoot] = useState(resolveNotesPortalRoot);
  const voiceSupported = checkBrowserCapability().ok;
  const {
    listening: voiceListening,
    onPartial: onVoicePartial,
    onTranscript: onVoiceTranscript,
    onListeningChange: onVoiceListeningChange,
    resetBuffer: resetVoiceBuffer,
    markEditing: markVoiceEditing,
  } = useLiveVoiceTranscript({
    value: draft,
    setValue: setDraft,
    appendMode: true,
  });

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const videoPreviewRef = useRef(null);
  const fileInputRef = useRef(null);

  const languageHint = currentAppLanguage(i18n) || "en";

  const clubPlayers = useMemo(
    () =>
      (players || []).filter(
        (p) => p?.name && !/^Seat\s/i.test(String(p.name))
      ),
    [players]
  );

  const resolvedViewerId = viewerUserId || readStoredUserId();
  const isOwnMatch = clubPlayers.some((p) =>
    isViewerPlayer(p, resolvedViewerId)
  );
  const canAddNote = Boolean(resolvedViewerId) && isOwnMatch;
  const canAddFeedback = Boolean(resolvedViewerId) && !isOwnMatch;
  const defaultKind = canAddNote ? "note" : "feedback";

  /** Singles: one target each. Doubles: one shared target for the pair. */
  const targets = useMemo(() => {
    if (clubPlayers.length >= 2) {
      return [
        {
          id: `pair-${side}-${clubPlayers.map((p) => p.seat ?? p.name).join("-")}`,
          players: clubPlayers,
          label: clubPlayers.map(playerLabel).join(" / "),
        },
      ];
    }
    return clubPlayers.map((player) => ({
      id: String(
        player.user_id ||
          player.player_id ||
          `${side}-${player.seat}-${player.name}`
      ),
      players: [player],
      label: playerLabel(player),
    }));
  }, [clubPlayers, side]);

  const relatedFor = useMemo(() => {
    const map = new Map();
    for (const target of targets) {
      const related = (notes || []).filter((n) => {
        if (rubberId && n.rubber_id && String(n.rubber_id) !== String(rubberId)) {
          return false;
        }
        return noteMatchesGroup(n, target.players, side);
      });
      map.set(target.id, related);
    }
    return map;
  }, [targets, notes, rubberId, side]);

  const openRelated = openPlayers
    ? relatedFor.get(
        targets.find(
          (t) =>
            t.players.length === openPlayers.length &&
            t.players.every((p, i) => {
              const o = openPlayers[i];
              if (p.user_id && o.user_id) {
                return String(p.user_id) === String(o.user_id);
              }
              return Number(p.seat) === Number(o.seat);
            })
        )?.id
      ) ||
      (notes || []).filter((n) => {
        if (rubberId && n.rubber_id && String(n.rubber_id) !== String(rubberId)) {
          return false;
        }
        return noteMatchesGroup(n, openPlayers, side);
      })
    : [];

  useEffect(() => {
    const sync = () => setPortalRoot(resolveNotesPortalRoot());
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  useEffect(() => {
    if (!openPlayers) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [openPlayers]);

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      stopStream(streamRef.current);
      if (mediaPreview && mediaPreview.startsWith("blob:")) {
        URL.revokeObjectURL(mediaPreview);
      }
    };
  }, [mediaPreview]);

  if ((!matchId && typeof createNote !== "function") || !targets.length) {
    return null;
  }

  const clearMedia = () => {
    clearInterval(timerRef.current);
    if (mediaRecorderRef.current?.state === "recording") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    stopStream(streamRef.current);
    streamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setRecording(false);
    setRecSeconds(0);
    if (mediaPreview && mediaPreview.startsWith("blob:")) {
      URL.revokeObjectURL(mediaPreview);
    }
    setMediaBlob(null);
    setMediaPreview("");
  };

  const openComposer = (targetPlayers, preferKind = defaultKind) => {
    clearMedia();
    setOpenPlayers(targetPlayers);
    const nextKind =
      preferKind === "note"
        ? canAddNote
          ? "note"
          : canAddFeedback
            ? "feedback"
            : "note"
        : canAddFeedback
          ? "feedback"
          : canAddNote
            ? "note"
            : "feedback";
    setKind(nextKind);
    setMode("text");
    resetVoiceBuffer();
    setSource("text");
    setError("");
  };

  const closeComposer = () => {
    clearMedia();
    setOpenPlayers(null);
    resetVoiceBuffer();
    setSource("text");
    setError("");
  };

  const switchMode = (next) => {
    clearMedia();
    setMode(next);
    setSource(next === "text" ? "text" : next);
    setError("");
  };

  const startRecording = async () => {
    setError("");
    clearMedia();
    try {
      const constraints =
        mode === "video"
          ? { audio: true, video: { facingMode: "user" } }
          : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (mode === "video" && videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.muted = true;
        await videoPreviewRef.current.play().catch(() => {});
      }
      const mimeCandidates =
        mode === "video"
          ? ["video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
          : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mime = mimeCandidates.find(
        (m) =>
          typeof MediaRecorder !== "undefined" &&
          MediaRecorder.isTypeSupported?.(m)
      );
      const recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data?.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const type =
          recorder.mimeType ||
          (mode === "video" ? "video/webm" : "audio/webm");
        const blob = new Blob(chunksRef.current, { type });
        stopStream(streamRef.current);
        streamRef.current = null;
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = null;
        }
        const url = URL.createObjectURL(blob);
        setMediaBlob(blob);
        setMediaPreview(url);
        setRecording(false);
        clearInterval(timerRef.current);
      };
      mediaRecorderRef.current = recorder;
      recorder.start(250);
      setRecording(true);
      setRecSeconds(0);
      timerRef.current = setInterval(() => {
        setRecSeconds((s) => s + 1);
      }, 1000);
      setSource(mode === "video" ? "video" : "audio");
    } catch (err) {
      setError(
        err?.message ||
          t(
            "cmMediaPermission",
            "Microphone or camera permission is required."
          )
      );
    }
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      setRecording(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    clearMedia();
    const url = URL.createObjectURL(file);
    setMediaBlob(file);
    setMediaPreview(url);
    const isVideo = String(file.type || "").startsWith("video/");
    setSource(isVideo ? "video" : "audio");
    setMode(isVideo ? "video" : "audio");
  };

  const canSave =
    !busy &&
    openPlayers?.length &&
    (mode === "text"
      ? Boolean(draft.trim())
      : Boolean(mediaBlob) || Boolean(draft.trim()));

  const save = async () => {
    if (!canSave || !openPlayers?.length) return;
    const allowedKind = kind === "note" ? canAddNote : canAddFeedback;
    const recipients =
      kind === "note"
        ? openPlayers.filter((p) => isViewerPlayer(p, resolvedViewerId))
        : openPlayers.filter((p) => !isViewerPlayer(p, resolvedViewerId));
    if (!allowedKind || !recipients.length) {
      setError(
        kind === "note"
          ? t("cmPrivateNoteOwnOnly", "Private notes are only for your own matches.")
          : t(
              "cmFeedbackOthersOnly",
              "You can only give feedback on other players' matches."
            )
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      let file = null;
      if (mediaBlob && (mode === "video" || mode === "audio")) {
        file =
          mediaBlob instanceof File
            ? mediaBlob
            : new File([mediaBlob], `feedback.webm`, {
                type: mediaBlob.type || `${mode}/webm`,
              });
      }
      const baseSource =
        file && (mode === "video" || mode === "audio")
          ? mode === "video"
            ? "video"
            : "audio"
          : source || "text";
      for (const player of recipients) {
        const payload = {
          kind,
          body: draft.trim(),
          source: baseSource,
          rubber_id: rubberId,
          about_side: side,
          about_seat: player.seat ?? null,
          about_user_id: player.user_id || null,
          about_player_id: player.player_id || null,
          about_display_name: playerLabel(player),
        };
        if (file) payload.file = file;
        if (typeof createNote === "function") {
          await createNote(payload);
        } else {
          await createChampionshipPlayerNote(matchId, payload);
        }
      }
      closeComposer();
      if (typeof onChanged === "function") await onChanged();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (noteId) => {
    if (busy) return;
    setBusy(true);
    try {
      if (typeof deleteNote === "function") {
        await deleteNote(noteId);
      } else {
        await deleteChampionshipPlayerNote(matchId, noteId);
      }
      if (typeof onChanged === "function") await onChanged();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const openLabel = openPlayers?.map(playerLabel).join(" / ") || "—";
  const opponentPlayers = namedPlayers(opponents);

  const renderTitleSide = (list) =>
    (list || []).map((player, idx) => {
      const rating = playerRatingMeta(player, t);
      const team = String(player.team || player.club || "").trim();
      return (
        <span
          key={`${player.user_id || player.player_id || player.name}-${idx}`}
          className="cm-note-dialog__player"
        >
          <strong>{playerLabel(player)}</strong>
          {rating ? (
            <span className="cm-note-dialog__rating">{rating}</span>
          ) : null}
          {team ? (
            <span className="cm-note-dialog__team">{team}</span>
          ) : null}
        </span>
      );
    });

  const dialog =
    openPlayers && typeof document !== "undefined"
      ? createPortal(
          <div
            className={`cm-note-dialog${isLightTheme ? " is-light" : ""}`}
            role="presentation"
            onClick={closeComposer}
            style={brandVars}
          >
            <div
              className="cm-note-dialog__sheet"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cm-note-dialog-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="cm-note-dialog__grab" aria-hidden />
              <header className="cm-note-dialog__head">
                <div
                  className={`cm-note-dialog__who${
                    opponentPlayers.length ? " has-matchup" : ""
                  }`}
                >
                  {opponentPlayers.length ? null : (
                    <span className="cm-note-dialog__avatar" aria-hidden>
                      {initials(openLabel)}
                    </span>
                  )}
                  <div>
                    <p className="cm-note-dialog__kicker">
                      {isOwnMatch
                        ? t("cmPrivateNote", "My note")
                        : t("cmAddFeedback", "Add feedback")}
                    </p>
                    <h2 id="cm-note-dialog-title">
                      <span className="cm-note-dialog__side">
                        {renderTitleSide(openPlayers)}
                      </span>
                      {opponentPlayers.length ? (
                        <>
                          <span className="cm-note-dialog__vs">
                            {t("competitionVs", "vs")}
                          </span>
                          <span className="cm-note-dialog__side">
                            {renderTitleSide(opponentPlayers)}
                          </span>
                        </>
                      ) : null}
                    </h2>
                  </div>
                </div>
                <div className="cm-note-dialog__head-actions">
                  {typeof onPlayMatchMedia === "function" &&
                  matchMediaCount > 0 ? (
                    <button
                      type="button"
                      className="cm-note-dialog__media-play"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onPlayMatchMedia();
                      }}
                      title={t(
                        "competitionStdPlayMatchMedia",
                        "Play match media"
                      )}
                      aria-label={t(
                        "competitionStdPlayMatchMedia",
                        "Play match media"
                      )}
                    >
                      <Clapperboard size={16} strokeWidth={2.2} aria-hidden />
                      {matchMediaCount > 1 ? (
                        <span>{matchMediaCount}</span>
                      ) : null}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="cm-note-dialog__close"
                    onClick={closeComposer}
                    aria-label={t("close", "Close")}
                  >
                    <X size={20} aria-hidden />
                  </button>
                </div>
              </header>

              {(canAddNote || canAddFeedback) ? (
                <div className="cm-note-dialog__kinds" role="group">
                  {canAddNote ? (
                    <button
                      type="button"
                      className={kind === "note" ? "is-on" : ""}
                      onClick={() => setKind("note")}
                    >
                      <StickyNote size={14} aria-hidden />
                      {t("cmPrivateNote", "My note")}
                    </button>
                  ) : null}
                  {canAddFeedback ? (
                    <button
                      type="button"
                      className={kind === "feedback" ? "is-on" : ""}
                      onClick={() => setKind("feedback")}
                    >
                      <MessageSquare size={14} aria-hidden />
                      {t("cmFeedback", "Feedback")}
                    </button>
                  ) : null}
                </div>
              ) : null}
              <p className="cm-note-dialog__hint">
                {kind === "note"
                  ? t(
                      "cmPrivateNoteHintOwn",
                      "Only you see this note about your match."
                    )
                  : t(
                      "cmFeedbackHintClub",
                      "Shared with club players and coaches — about your club teammate only."
                    )}
              </p>

              {openRelated.length ? (
                <ul className="cm-note-dialog__existing">
                  {openRelated.map((n) => {
                    const media = resolveMediaUrl(n.media_url);
                    const isVideo =
                      n.source === "video" ||
                      /\.(mp4|webm|mov)(\?|$)/i.test(media);
                    const isAudio =
                      !isVideo &&
                      (n.source === "audio" ||
                        n.source === "voice" ||
                        /\.(webm|mp3|m4a|ogg|wav)(\?|$)/i.test(media));
                    return (
                      <li
                        key={n.note_id}
                        className={
                          n.kind === "note" ? "is-note" : "is-feedback"
                        }
                      >
                        <div className="cm-note-dialog__existing-head">
                          <span>
                            {n.kind === "note"
                              ? t("cmPrivateNote", "My note")
                              : n.mine
                                ? t("cmMyFeedback", "My feedback")
                                : n.author_name ||
                                  t("cmFeedback", "Feedback")}
                          </span>
                          {n.mine ? (
                            <button
                              type="button"
                              aria-label={t("cmDeleteNote", "Delete note")}
                              onClick={() => remove(n.note_id)}
                            >
                              <Trash2 size={12} aria-hidden />
                            </button>
                          ) : null}
                        </div>
                        {media && isVideo ? (
                          <video src={media} controls playsInline />
                        ) : null}
                        {media && isAudio ? (
                          <audio src={media} controls />
                        ) : null}
                        {n.body &&
                        !/^\[(Video|Audio|Attachment) feedback\]$/i.test(
                          n.body
                        ) &&
                        n.body !== "[Attachment]" ? (
                          <p>{n.body}</p>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              ) : null}

              <div className="cm-note-dialog__modes" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "text"}
                  className={mode === "text" ? "is-on" : ""}
                  onClick={() => switchMode("text")}
                >
                  <Type size={15} aria-hidden />
                  {t("cmModeText", "Text")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "audio"}
                  className={mode === "audio" ? "is-on" : ""}
                  onClick={() => switchMode("audio")}
                >
                  <Mic size={15} aria-hidden />
                  {t("cmModeAudio", "Audio")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "video"}
                  className={mode === "video" ? "is-on" : ""}
                  onClick={() => switchMode("video")}
                >
                  <Video size={15} aria-hidden />
                  {t("cmModeVideo", "Video")}
                </button>
              </div>

              <div className="cm-note-dialog__body">
                {mode === "text" ? (
                  <>
                    <textarea
                      value={draft}
                      onChange={(e) => {
                        markVoiceEditing();
                        setDraft(e.target.value);
                        setSource("text");
                      }}
                      rows={5}
                      placeholder={
                        voiceListening
                          ? t("cmDictateListening", "Listening…")
                          : t(
                              "cmNotePlaceholder",
                              "Strengths, serves, weak wing…"
                            )
                      }
                      autoFocus
                    />
                    {voiceSupported ? (
                      <div className="cm-note-dialog__dictate">
                        <span>
                          {voiceListening
                            ? t("cmDictateListening", "Listening…")
                            : t("cmDictate", "Dictate")}
                        </span>
                        <CoachVoiceButton
                          languageHint={languageHint}
                          primaryColor={primaryColor}
                          disabled={busy}
                          onPartial={onVoicePartial}
                          onListeningChange={onVoiceListeningChange}
                          onTranscript={(payload) => {
                            onVoiceTranscript(payload);
                            if (String(payload?.text || "").trim()) {
                              setSource("voice");
                            }
                          }}
                        />
                      </div>
                    ) : null}
                  </>
                ) : null}

                {mode === "audio" || mode === "video" ? (
                  <div className={`cm-note-dialog__media is-${mode}`}>
                    {mode === "video" ? (
                      <div className="cm-note-dialog__stage">
                        {mediaPreview && !recording ? (
                          <video
                            src={mediaPreview}
                            controls
                            playsInline
                            className="cm-note-dialog__playback"
                          />
                        ) : (
                          <video
                            ref={videoPreviewRef}
                            playsInline
                            muted
                            className="cm-note-dialog__live"
                          />
                        )}
                        {!recording && !mediaPreview ? (
                          <p className="cm-note-dialog__stage-hint">
                            {t(
                              "cmVideoHint",
                              "Record a short clip or pick a file from your phone."
                            )}
                          </p>
                        ) : null}
                      </div>
                    ) : (
                      <div className="cm-note-dialog__stage cm-note-dialog__stage--audio">
                        {mediaPreview && !recording ? (
                          <audio src={mediaPreview} controls />
                        ) : (
                          <div
                            className={`cm-note-dialog__wave${
                              recording ? " is-live" : ""
                            }`}
                            aria-hidden
                          >
                            <i />
                            <i />
                            <i />
                            <i />
                            <i />
                          </div>
                        )}
                        <p className="cm-note-dialog__stage-hint">
                          {recording
                            ? t("cmRecordingSeconds", "Recording… {{s}}s", {
                                s: recSeconds,
                              })
                            : mediaPreview
                              ? t(
                                  "cmAudioReady",
                                  "Audio ready — add a caption if you like."
                                )
                              : t(
                                  "cmAudioHint",
                                  "Tap record for a voice note, or attach an audio file."
                                )}
                        </p>
                      </div>
                    )}

                    <div className="cm-note-dialog__rec-bar">
                      {!recording ? (
                        <button
                          type="button"
                          className="cm-note-dialog__rec"
                          onClick={startRecording}
                          disabled={busy}
                        >
                          {mode === "video" ? (
                            <Video size={18} aria-hidden />
                          ) : (
                            <Mic size={18} aria-hidden />
                          )}
                          {mediaPreview
                            ? t("cmReRecord", "Re-record")
                            : t("cmRecord", "Record")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="cm-note-dialog__rec is-stop"
                          onClick={stopRecording}
                        >
                          <Square size={16} aria-hidden />
                          {t("cmStop", "Stop")} · {recSeconds}s
                        </button>
                      )}
                      <button
                        type="button"
                        className="cm-note-dialog__pick"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={busy || recording}
                      >
                        <ImagePlus size={16} aria-hidden />
                        {t("cmPickFile", "File")}
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={mode === "video" ? "video/*" : "audio/*"}
                        capture={mode === "video" ? "user" : undefined}
                        hidden
                        onChange={onPickFile}
                      />
                    </div>

                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={2}
                      placeholder={t(
                        "cmCaptionPlaceholder",
                        "Optional caption…"
                      )}
                    />
                  </div>
                ) : null}
              </div>

              {error ? <p className="cm-note-dialog__err">{error}</p> : null}

              <footer className="cm-note-dialog__foot">
                <button
                  type="button"
                  className="cm-note-dialog__cancel"
                  onClick={closeComposer}
                  disabled={busy}
                >
                  {t("cancel", "Cancel")}
                </button>
                <button
                  type="button"
                  className="cm-note-dialog__save"
                  disabled={!canSave}
                  onClick={save}
                >
                  {busy
                    ? t("cmSaving", "Saving…")
                    : t("cmSaveNote", "Save")}
                </button>
              </footer>
            </div>
          </div>,
          portalRoot || document.body
        )
      : null;

  return (
    <div
      className={`cm-player-notes cm-player-notes--compact cm-player-notes--${align}`}
      style={brandVars}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {targets.map((target) => {
        const count = relatedFor.get(target.id)?.length || 0;
        const iconLabel = isOwnMatch
          ? t("cmAddNoteAbout", "Note for {{name}}", { name: target.label })
          : t("cmAddFeedbackAbout", "Feedback for {{name}}", {
              name: target.label,
            });
        return (
          <button
            key={target.id}
            type="button"
            className={`cm-player-notes__icon-btn${
              count ? " has-notes" : ""
            }`}
            title={iconLabel}
            aria-label={iconLabel}
            onPointerDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openComposer(target.players, defaultKind);
            }}
          >
            {isOwnMatch ? (
              <StickyNote size={15} strokeWidth={2.2} aria-hidden />
            ) : (
              <MessageSquare size={15} strokeWidth={2.2} aria-hidden />
            )}
            {count ? <span>{count}</span> : null}
          </button>
        );
      })}
      {dialog}
    </div>
  );
}
